/**
 * Types for Contract Intelligence, as cat-material serves them.
 *
 * One contract is one bundle: the documents that
 * cat-contract-extract grouped, with a full snapshot of its terms and price
 * lines per period (a version), the parties its documents name, and the
 * transactions linkage linked to it. cat-material imports it whole and serves
 * it read-only; these mirror `contracts/interface/api/serializers.py`
 * verbatim (snake_case).
 */

/**
 * The bundle's single lifecycle state, computed by the backend from its
 * stage and the dates its versions span, against a reference date:
 * draft → signed (not yet started) → active → expiring → expired.
 */
export type ContractLifecycle = 'draft' | 'signed' | 'active' | 'expiring' | 'expired';

export const LIFECYCLES: ContractLifecycle[] = ['draft', 'signed', 'active', 'expiring', 'expired'];

export const LIFECYCLE_LABEL: Record<ContractLifecycle, string> = {
    draft: 'Draft',
    signed: 'Signed',
    active: 'Active',
    expiring: 'Expiring',
    expired: 'Expired',
};

export const LIFECYCLE_NOTE: Record<ContractLifecycle, string> = {
    draft: 'The base document carries no signature.',
    signed: 'Signed; its first version has not started yet.',
    active: 'In force.',
    expiring: 'Ends within 90 days.',
    expired: 'Its last version has ended.',
};

export const LIFECYCLE_COLOR = {
    draft: 'default',
    signed: 'info',
    active: 'success',
    expiring: 'warning',
    expired: 'error',
} as const;

/** `action` stops the bundle being linked to its spend; `watch` only limits what can be read. */
export type Gap = {key: string; label: string; severity: 'action' | 'watch'; note: string};

export type LinkStatus = 'linked' | 'competing' | 'candidate';

export const LINK_STATUSES: LinkStatus[] = ['linked', 'competing', 'candidate'];

export type ContractRow = {
    id: number;
    bundle_id: string;
    label: string;
    title: string;
    /** The base document's type (`master_agreement`, `order_form` …). */
    document_type: string;
    contract_type: string;
    supplier_name: string;
    buying_entities: string[];
    stage: 'signed' | 'draft';
    lifecycle: ContractLifecycle;
    /** ISO yyyy-mm-dd: the first version's start, the last version's stated end. */
    starts_on: string | null;
    ends_on: string | null;
    /** What the end is: the stated-end column, `termination`, or `open`. */
    end_basis: string;
    /** The currency the last version's lines state. */
    currency: string;
    linkage_state: '' | 'linked' | 'not_ready' | 'no_spend' | 'failed';
    gaps: Gap[];
    needs_attention: boolean;
    document_count: number;
    version_count: number;
    /** The spend of the transactions this bundle owns, in the spend currency. */
    linked_spend: number;
    /** Its opportunities' estimated saving, each times its confidence; 0 without a leakage. */
    weighted_saving: number;
};

export type ImportInfo = {
    project: string;
    run_id: string;
    schema_version: string;
    bundling_pipeline_id: string;
    /** The party linkage that resolved the parties; '' for an export made before linkage was split in two. */
    party_linkage_pipeline_id: string;
    /** The spend matching the export came from. */
    linkage_pipeline_id: string;
    exported_at: string;
    imported_at: string;
    currency: string;
};

/** `GET /data/{databag_id}/contracts/summary/`: whole-databag figures, independent of paging and filters. */
export type ContractSummaryResponse = {
    as_of: string;
    lifecycle_counts: Record<ContractLifecycle, number>;
    type_counts: {document_type: string; count: number}[];
    bundles: number;
    documents: number;
    needs_attention: number;
    linked_spend: number;
    /** What the spend amounts are in. */
    currency: string;
    latest_import: ImportInfo | null;
};

/** One value of a version: copied from a document, with what changed since the version before. */
export type Cell = {
    value: unknown;
    change: 'unchanged' | 'changed' | 'added' | 'removed';
    previous?: unknown;
    document_id: string;
    source_row: string;
};

/** item (table) → line (`L1`…, `''` for the header) → field → cell. */
export type VersionValues = Record<string, Record<string, Record<string, Cell>>>;

export type ContractVersion = {
    version_no: number;
    valid_from: string | null;
    /** The date column the start came from; '' when undated. */
    from_basis: string;
    from_document: string;
    /** Exclusive when `to_basis` is `next version`. */
    valid_to: string | null;
    to_basis: string;
    documents: string[];
    changed: number;
    warnings: string[];
    values: VersionValues;
};

export type ContractDocument = {
    /** Its `[n]`. */
    position: number;
    document_id: string;
    file_name: string;
    relative_path: string;
    uri: string;
    /** The bundling role: `opens`, `reference` … */
    role: string;
    note: string;
    /** Set on a framework linked in from another bundle. */
    shared_from: string;
    /** The document's extracted header row. */
    header: Record<string, unknown> | null;
    /** The rows of its other tables by name (price tiers, other amounts, references), which no version holds. */
    tables: Record<string, Record<string, unknown>[]>;
    /** Copied into the media bucket (`store_contract_documents`), so it can be opened. */
    has_file: boolean;
};

export type ContractParty = {
    name: string;
    role: 'supplier' | 'buyer' | 'other' | 'unknown';
    documents: string[];
    stated: Record<string, string>;
    evidence: string;
    note: string;
    /** The role linkage used it in; '' when unused. */
    used_as: string;
    /** Used in the role opposite to the one stated. */
    corrected: boolean;
};

export type ContractLinkage = {
    state: ContractRow['linkage_state'];
    missing: string[];
    supplier_label: string;
    supplier_id: number | null;
    parent_supplier_id: number | null;
    entities: string[];
    scope_from: string | null;
    rows_in_scope: number;
    method: string;
    summary: string;
    warnings: string[];
};

/** `GET /data/{databag_id}/contracts/{id}/` */
export type ContractDetail = ContractRow & {
    databag_id: number;
    folder: string;
    summary: string;
    scope: string;
    as_of: string;
    /** The version in force on `as_of`; null without versions. */
    version_in_force: number | null;
    documents: ContractDocument[];
    versions: ContractVersion[];
    parties: ContractParty[];
    linkage: ContractLinkage;
    link_counts: Record<LinkStatus, number>;
    /** Linked spend per line; `''` is the bundle as a whole. */
    purchases: {line: string; spend: number; links: number}[];
    spend_currency: string;
    /** The spend's description columns linkage read (`part__p_description` …), each a key of `ContractLink.columns`. */
    spend_columns: string[];
    /** Every kind's opportunities on the bundle, in order; empty when the import brought none. */
    opportunity_counts: OpportunityCount[];
};

/**
 * The kind of leakage one of cat-contract-extract's specialist agents looked
 * for. A leakage has an estimated saving; a risk only an amount at risk.
 */
export type OpportunityKind =
    | 'price_above_contract' | 'amendment_price_not_applied' | 'tier_not_applied' | 'shorter_payment_terms'
    | 'duplicate_invoice' | 'outside_period' | 'off_contract_supplier' | 'maverick';

export type OpportunityCategory = 'leakage' | 'risk';

/** ran: the specialist checked the bundle; not_met: the contract or data lacks what it needs; failed: no answer. */
export type CheckState = 'ran' | 'not_met' | 'failed';

export const KIND_LABEL: Record<OpportunityKind, string> = {
    price_above_contract: 'Billed above contract price',
    amendment_price_not_applied: 'Amendment price not applied',
    tier_not_applied: 'Volume tier not applied',
    shorter_payment_terms: 'Invoiced on shorter payment terms',
    duplicate_invoice: 'Possible duplicate invoice',
    outside_period: 'Spend outside the contract period',
    off_contract_supplier: 'Off-contract spend with the supplier',
    maverick: 'Maverick spend',
};

export const KIND_CATEGORY: Record<OpportunityKind, OpportunityCategory> = {
    price_above_contract: 'leakage',
    amendment_price_not_applied: 'leakage',
    tier_not_applied: 'leakage',
    shorter_payment_terms: 'leakage',
    duplicate_invoice: 'leakage',
    outside_period: 'risk',
    off_contract_supplier: 'risk',
    maverick: 'risk',
};

export const CHECK_STATE_LABEL: Record<CheckState, string> = {
    ran: 'Checked',
    not_met: 'Not applicable',
    failed: 'Failed',
};

/** One kind on the bundle: its findings, its total and its check; `state` null when its specialist did not run. */
export type OpportunityCount = {
    kind: OpportunityKind;
    findings: number;
    estimated_saving: number;
    amount_at_risk: number;
    state: CheckState | null;
};

/** A contract value a finding is measured against; `ref` is `<document_id>:<table>:<row key>`. */
export type ContractEvidence = {ref: string; role: string; file: string | null; field: string | null; value: unknown};

/** `GET /data/{databag_id}/contracts/{id}/opportunities/` */
export type ContractOpportunity = {
    id: number;
    opportunity_id: string;
    kind: OpportunityKind;
    category: OpportunityCategory;
    supplier: string;
    entities: string[];
    period_from: string | null;
    period_to: string | null;
    spend: number;
    currency: string;
    /** Null for a risk. */
    estimated_saving: number | null;
    /** The saving for a leakage, the spend for a risk. */
    amount_at_risk: number;
    confidence: number;
    summary: string;
    /** Markdown. */
    analysis: string;
    /** Markdown. */
    recommended_action: string;
    facts: Record<string, unknown>;
    warnings: string[];
    contract_evidence: ContractEvidence[];
};

/** A transaction a finding stands on. */
export type OpportunityRow = {
    part_id: number;
    p_id: string;
    date: string | null;
    description: string;
    supplier: string;
    spend: number | null;
    role: string;
    amount: number | null;
    /** Its share of the estimated saving. */
    saving: number | null;
    facts: Record<string, unknown>;
};

/** `GET /data/{databag_id}/contracts/{id}/opportunities/{pk}/` */
export type ContractOpportunityDetail = ContractOpportunity & {rows: OpportunityRow[]};

/** `GET /data/{databag_id}/contracts/{id}/opportunities/checks/` */
export type ContractCheck = {kind: OpportunityKind; state: CheckState; missing: string[]; findings: number; saving: number};

export type Rival = {contract_id: number; title: string; status: LinkStatus; confidence: number};

/** `GET /data/{databag_id}/contracts/{id}/links/` */
export type ContractLink = {
    id: number;
    part_id: number;
    p_id: string;
    date: string | null;
    description: string;
    supplier: string;
    entity: string;
    /** The transaction's value of each of the bundle's `spend_columns`. */
    columns: Record<string, string>;
    spend: number | null;
    /** The spend's quantity; null where it leaves it 0. */
    quantity: number | null;
    /** The spend's unit of measure; '' when it has none. */
    unit: string;
    /** The spend's unit price, in `spend_currency`; null where it leaves it 0. */
    unit_price: number | null;
    /** `L1`…; `''` links the bundle as a whole. */
    line: string;
    version_no: number;
    confidence: number;
    reason: string;
    status: LinkStatus;
    /** The other bundles' links on the same transaction. */
    competing_with: Rival[];
};
